import { LinearProgress } from '@mui/joy';

import { useEffect, useRef } from 'react';
import { Link, Outlet } from 'react-router-dom';

import { ErrorBanner } from '@/components/ErrorBanner';
import { useDatadogContext } from '@/context/hooks/useDatadogContext';
import { useGetLSUserIdFromUser } from '@/hooks/useGetLSUserIdFromUser';

import {
  useOnboardingState,
  useOrganizationId,
  useTenantList,
  useUser,
} from '../../hooks/useSwr';

export function OrganizationLayout() {
  const { datadogLogs } = useDatadogContext();

  const tenantList = useTenantList();
  const organizationId = useOrganizationId();
  const selectedTenant = tenantList.data?.find(
    (tenant) => tenant.id === organizationId
  );

  const lastTenant = useRef<string | null>(null);
  useEffect(() => {
    const newTenantId = selectedTenant?.id ?? null;
    if (selectedTenant) {
      datadogLogs?.setGlobalContextProperty('metadata', {
        tenant_id: selectedTenant.id,
      });
    }

    lastTenant.current = newTenantId;
  }, [selectedTenant, datadogLogs]);

  const onboardingState = useOnboardingState();
  const { data: user, isLoading: userLoading } = useUser();
  const { isLoading: lsUserIdLoading } = useGetLSUserIdFromUser(
    user?.data.user?.id
  );
  // wait for both user and lsUserId to load
  const isLoadingUser = userLoading || lsUserIdLoading;

  if (
    (tenantList.isLoading && !organizationId) ||
    onboardingState.isLoading ||
    isLoadingUser
  ) {
    return (
      <div className="px-4 py-6">
        <LinearProgress />
      </div>
    );
  }

  if (tenantList.data?.length === 0) {
    return (
      <ErrorBanner className="mt-6">
        <div>
          Error: You have no workspaces in this organization. Please{' '}
          <Link
            className="underline"
            target="_blank"
            to="https://api.smith.langchain.com/redoc#tag/workspaces/operation/create_workspace_api_v1_workspaces_post"
          >
            create a new workspace
          </Link>
          . If you need assistance, contact support at support@langchain.dev.
        </div>
      </ErrorBanner>
    );
  }

  return <Outlet />;
}
